/**
 * [#662] Tenant Lead Portal — page-area vocabulary for the grant axis.
 *
 * Grants are an authorization dimension SEPARATE from roles (#668): access =
 * canonical role AND per-tenant page grant. The seven page areas mirror the
 * epic's seven portal pages; `admin_matrix` is internal/NU-GUI-only and is
 * role-gated (admin-tier canonical roles), never grant-gated — it is
 * excluded from the grantable set and from the admin grant-matrix API.
 *
 * Grant flavors: `read` (GET only) vs `write` (everything) — the design
 * brief's Compliance-Officer read-only semantics are a grant FLAVOR, not a
 * new role name (persona names never gate).
 */

/** All seven portal page areas (epic #669). */
export const PORTAL_PAGE_AREAS = [
  "portal_home",
  "lead_requests",
  "deliveries",
  "results_reports",
  "optout_returns",
  "complaints",
  "admin_matrix",
] as const;

export type PortalPageArea = (typeof PORTAL_PAGE_AREAS)[number];

/** The areas a tenant can be granted — admin_matrix is internal-only. */
export const GRANTABLE_PAGE_AREAS = [
  "portal_home",
  "lead_requests",
  "deliveries",
  "results_reports",
  "optout_returns",
  "complaints",
] as const;

export type GrantablePageArea = (typeof GRANTABLE_PAGE_AREAS)[number];

const GRANTABLE_SET: ReadonlySet<string> = new Set(GRANTABLE_PAGE_AREAS);

export function isGrantablePageArea(
  value: unknown,
): value is GrantablePageArea {
  return typeof value === "string" && GRANTABLE_SET.has(value);
}

/** Stored grant flavors. `none` never hits the table — absence of a row. */
export const GRANT_ACCESS_VALUES = ["read", "write"] as const;

export type GrantAccess = (typeof GRANT_ACCESS_VALUES)[number];

/** Matrix cell value in the admin API: none | read | write. */
export type GrantMatrixValue = GrantAccess | "none";

export function isGrantMatrixValue(value: unknown): value is GrantMatrixValue {
  return value === "none" || value === "read" || value === "write";
}

/** Full per-tenant matrix over the grantable areas. */
export type GrantMatrix = Record<GrantablePageArea, GrantMatrixValue>;

export function emptyGrantMatrix(): GrantMatrix {
  const matrix = {} as GrantMatrix;
  for (const area of GRANTABLE_PAGE_AREAS) {
    matrix[area] = "none";
  }
  return matrix;
}
